import { TextInput } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { Segmented } from '../v2/Segmented'
import { FileDropzone } from './FileDropzone'
import css from '../../styles/EditorV2.module.css'
import classes from './GenerationForm.module.css'

const KINDS = [
  { value: 'social', labelKey: 'editorV2.generation.source.social' },
  { value: 'website', labelKey: 'editorV2.generation.source.website' },
  { value: 'files', labelKey: 'editorV2.generation.source.files' },
]

const BELOW = ['label', 'input', 'description', 'error']

export function filesHelp(maxLabel) {
  return i18n.t('editorV2.generation.source.filesHelp', { size: String(maxLabel || '20 MB').replace(' ', '') })
}

/**
 * "Source (optional)" (Figma "Sources", 127:14076): a segment choosing the social post URL,
 * a website link or the PDF/image upload. "PDF or Images" edits the same file list as the
 * "Files (optional)" block, which the form hides while this option is selected.
 */
export function SourceSelector({ form, disabled = false, stacked = false }) {
  const { t } = useTranslation()
  return (
    <div className={classes.field}>
      <span className={css.label} style={{ marginBottom: 0 }}>
        {t('editorV2.generation.source.label')} <span className={css.optional}>{t('editorV2.generation.form.optional')}</span>
      </span>
      <Segmented
        ariaLabel={t('editorV2.generation.source.label')}
        value={form.sourceKind}
        onChange={form.setSourceKind}
        options={KINDS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
        disabled={disabled}
        stack={stacked}
        fullWidth={stacked}
        className={classes.sourceSegment}
      />

      {form.sourceKind === 'social' && (
        <TextInput
          aria-label={t('editorV2.generation.source.socialUrl')}
          placeholder={t('editorV2.generation.source.socialPlaceholder')}
          description={t('editorV2.generation.source.socialHelp')}
          inputWrapperOrder={BELOW}
          type="url"
          value={form.socialUrl}
          onChange={(event) => form.setSocialUrl(event.currentTarget.value)}
          disabled={disabled}
          classNames={{ description: classes.below }}
        />
      )}

      {form.sourceKind === 'website' && (
        <TextInput
          aria-label={t('editorV2.generation.source.websiteUrl')}
          placeholder={t('editorV2.generation.form.websitePlaceholder')}
          description={t('editorV2.generation.source.websiteHelp')}
          inputWrapperOrder={BELOW}
          type="url"
          value={form.websiteUrl}
          onChange={(event) => form.setWebsiteUrl(event.currentTarget.value)}
          disabled={disabled}
          classNames={{ description: classes.below }}
        />
      )}

      {form.sourceKind === 'files' && (
        <FilesBody form={form} disabled={disabled} />
      )}
    </div>
  )
}

function FilesBody({ form, disabled }) {
  const { t } = useTranslation()
  return (
    <div className={classes.filesBody}>
      <FileDropzone
        files={form.files}
        onAdd={form.addFiles}
        onRemove={form.removeFile}
        disabled={disabled}
        label={t('editorV2.generation.source.filesLabel')}
      />
      {form.fileError && <span className={classes.error} role="alert">{form.fileError}</span>}
      <span className={`${css.help} ${classes.below}`}>{filesHelp(form.fileMaxLabel)}</span>
    </div>
  )
}

/** "Files (optional)": supporting images or a print ad attached next to any source. */
export function FilesField({ form, disabled = false }) {
  const { t } = useTranslation()
  return (
    <div className={classes.filesField}>
      <span className={css.label} style={{ marginBottom: 0 }}>
        {t('editorV2.generation.source.filesTitle')} <span className={css.optional}>{t('editorV2.generation.form.optional')}</span>
      </span>
      <FilesBody form={form} disabled={disabled} />
    </div>
  )
}
